import { useMutation } from "react-query";
import { CatalogItemObject, WareCatalogItem } from "types";
import { api } from "@/api/api";
import { useInvalidate } from "@/hooks/useInvalidation";
import { Response } from "@/types/Response";
import { getSeller } from "@/utils/getSeller";
import { updateCache } from "@/utils/updateCache";

export interface LotOperation {
    id: string;
    op: "increment" | "permanent_removal";
    data: {
        id: string;
        field: string;
        value: number;
        reason: string;
        comment?: string;
    };
}

export interface AdjustLotResult {
    id: string;
    status: "success" | "error";
    error?: { field: string; reason: string; message: string };
}

export interface AdjustLotResponse {
    results: AdjustLotResult[];
}

interface AdjustLotParams {
    errorStrategy?: "stop_on_failure" | "continue_on_failure";
    ops: LotOperation[];
    productId?: ID;
}

export const fetchWareCatalogItemsByProduct = async (productId: ID): Promise<WareCatalogItem[]> => {
    const seller = getSeller();
    const { data } = await api.get<Response<WareCatalogItem>>("/catalogItems", {
        params: {
            "product[in]": productId,
            object: CatalogItemObject.WARE_CATALOG_ITEM,
            expand: "inventory.inventoryLots",
            seller: seller.id,
            limit: 100,
        },
    });

    return data.data ?? [];
};

export const refreshWareCatalogItemsCache = async (productId: ID): Promise<WareCatalogItem[]> => {
    const refreshedCatalogItems = await fetchWareCatalogItemsByProduct(productId);

    if (refreshedCatalogItems.length > 0) {
        updateCache("catalogItems", refreshedCatalogItems, false);
    }

    return refreshedCatalogItems;
};

export const useAdjustInventoryLot = () => {
    const invalidate = useInvalidate();

    return useMutation(
        async ({ errorStrategy = "stop_on_failure", ops, productId }: AdjustLotParams): Promise<AdjustLotResponse> => {
            const { data } = await api.patch<AdjustLotResponse>("/wareInventoryLots", { errorStrategy, ops });

            if (productId) {
                await refreshWareCatalogItemsCache(productId);

                invalidate("products", productId);
            } else {
                // Fallback: if no productId, invalidate all catalogItems
                invalidate("catalogItems");
            }

            return data;
        },
    );
};
